import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

interface SkeletonDivProps {
  width?: string;
  height?: string;
}

const SkeletonDiv = styled.div<SkeletonDivProps>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '20px'};
  background: linear-gradient(
    90deg,
    #e0e0e0 25%,
    #f0f0f0 50%,
    #e0e0e0 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 2px;
  margin-bottom: 8px;
`;

interface SkeletonLoaderProps {
  width?: string;
  height?: string;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ width, height }) => {
  return <SkeletonDiv width={width} height={height} />;
};

export default SkeletonLoader;

const FacetContainer = styled.div`
  width: 250px;
  padding: 15px;
  padding-left: 0;
`;

const Facet = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 20px;
`;

const FacetList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const FacetItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const CheckboxSkeleton = styled(SkeletonDiv)`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

interface FacetSkeletonLoaderProps {
  numberOfFacets?: number;
  numberOfOptions?: number;
}

export const FacetSkeletonLoader: React.FC<FacetSkeletonLoaderProps> = ({
  numberOfFacets = 1,
  numberOfOptions = 6,
}) => {
  return (
    <FacetContainer>
      {[...Array(numberOfFacets)].map((_, i) => (
        <Facet key={i}>
          <SkeletonLoader width="60%" height="24px" />
          <FacetList>
            {[...Array(numberOfOptions)].map((_, j) => (
              <FacetItem key={j}>
                <CheckboxSkeleton />
                <SkeletonLoader width="80%" height="20px" />
              </FacetItem>
            ))}
          </FacetList>
        </Facet>
      ))}
    </FacetContainer>
  );
};

const ProductSuggestionContainer = styled.div`
  width: 100%;
  padding: 15px 0;
`;

const ProductSuggestion = styled.div`
  display: flex;
  margin-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
`;

const ImagePlaceholder = styled(SkeletonDiv)`
  width: 50px;
  height: 50px;
  margin-right: 15px;
  flex-shrink: 0;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const TitleSkeleton = styled(SkeletonDiv)`
  height: 18px;
  margin-bottom: 5px;
`;

const ErpNumberSkeleton = styled(SkeletonDiv)`
  height: 14px;
  width: 33%;
`;

interface ProductSuggestionSkeletonLoaderProps {
  numberOfSuggestions?: number;
}

export const ProductSuggestionSkeletonLoader: React.FC<ProductSuggestionSkeletonLoaderProps> = ({
  numberOfSuggestions = 1,
}) => {
  return (
    <ProductSuggestionContainer>
      {[...Array(numberOfSuggestions)].map((_, index) => (
        <ProductSuggestion key={index}>
          <ImagePlaceholder />
          <ProductInfo>
            <TitleSkeleton />
            <ErpNumberSkeleton />
          </ProductInfo>
        </ProductSuggestion>
      ))}
    </ProductSuggestionContainer>
  );
};